'use client';

import { Permission } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { FeedbackInboxPanel } from '@/core/components/feedback/feedback-inbox-panel';
import { FeedbackInvitesPanel } from '@/core/components/feedback/feedback-invites-panel';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import { FeedbackPageShell } from '@/core/components/feedback/feedback-page-shell';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

export type FeedbackActivityTab = 'inbox' | 'sent';

function parseTab(value: string | null): FeedbackActivityTab {
  return value === 'sent' ? 'sent' : 'inbox';
}

export function FeedbackActivityPage() {
  const dict = useDictionary();
  const feedbackDict = dict.feedback;
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canInvite = can(Permission.FEEDBACK_INVITE);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = parseTab(searchParams.get('tab'));

  const setTab = (tab: FeedbackActivityTab) => {
    const params = new URLSearchParams(location.search);
    if (tab === 'inbox') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const query = params.toString();
    navigateShell(
      query
        ? `${shellPaths.feedbackActivity}?${query}`
        : shellPaths.feedbackActivity,
      { intent: 'updateQuery' },
    );
  };

  const tabs = useMemo(
    () => [
      {
        name: feedbackDict.activity.tabs.inbox,
        value: 'inbox',
        content: <FeedbackInboxPanel />,
      },
      {
        name: feedbackDict.activity.tabs.sent,
        value: 'sent',
        content: <FeedbackInvitesPanel />,
      },
    ],
    [feedbackDict.activity.tabs.inbox, feedbackDict.activity.tabs.sent],
  );

  return (
    <FeedbackPageShell
      title={feedbackDict.activity.title}
      breadcrumb={
        <FeedbackPageBreadcrumb
          domainLabel={dict.nav.navDomainWork}
          pageLabel={dict.nav.feedbackActivity}
        />
      }
      actionsLayout="stack"
      actions={
        canInvite ? (
          <Button
            type="button"
            size="sm"
            onClick={() => navigateShell(shellPaths.feedbackCompose)}
          >
            <Plus className="h-4 w-4" />
            {feedbackDict.invites.create}
          </Button>
        ) : null
      }
    >
      <AnimatedUnderlineTabs
        value={activeTab}
        onValueChange={(value) => setTab(parseTab(value))}
        className="w-full"
        tabs={tabs}
      />
    </FeedbackPageShell>
  );
}
